import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { assetImage } from "@/lib/media";
import { supabase } from "@/integrations/supabase/client";

import moreNutrition from "@/assets/brands/more-nutrition.png.asset.json";
import creamyFabrics from "@/assets/brands/creamy-fabrics.png.asset.json";
import swing2sleep from "@/assets/brands/swing2sleep.png.asset.json";
import esn from "@/assets/brands/esn.png.asset.json";
import newEra from "@/assets/brands/new-era.png.asset.json";
import wardow from "@/assets/brands/wardow.png.asset.json";

import raw from "@/assets/brands/raw.png.asset.json";
import legends from "@/assets/brands/legends.png.asset.json";
import elephantStock from "@/assets/brands/elephant-stock.png.asset.json";

type Brand = { name: string; src: string; invert: boolean };

// Partner-brand proof. These projects were delivered under our sister brand
// Datora — we surface them here to carry that trust over to DevCore.
// Logos are managed in the admin (Content → Hero-Logos); these bundled assets
// are the fallback used before the DB responds / if it is empty.
const fallbackBrands: Brand[] = [
  { name: "More Nutrition", src: moreNutrition.url, invert: true },
  { name: "Creamy Fabrics", src: creamyFabrics.url, invert: true },
  { name: "swing2sleep", src: swing2sleep.url, invert: true },
  { name: "ESN", src: esn.url, invert: true },
  { name: "New Era", src: newEra.url, invert: true },

  { name: "Wardow", src: wardow.url, invert: true },
  { name: "RAW", src: raw.url, invert: true },
  { name: "Legends", src: legends.url, invert: true },
  { name: "Elephant Stock", src: elephantStock.url, invert: true },
];

// h-6 (24px) display → ~48px for retina; keep a little headroom for wider marks.
const logoImg = (src: string) => assetImage(src, 200, { quality: 82, jpeg: false });

const TrustMarquee = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data: brands = fallbackBrands } = useQuery({
    queryKey: ["hero-logos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hero_logos")
        .select("name, logo_url, invert")
        .eq("published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      if (!data || data.length === 0) return fallbackBrands;
      return data.map((r) => ({ name: r.name, src: r.logo_url, invert: r.invert })) as Brand[];
    },
  });

  return (
    <div className="border-t border-border/70 bg-background/80 backdrop-blur-md">
      <div className="container py-5 md:py-6">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="group flex w-full flex-col gap-4 text-left lg:flex-row lg:items-center lg:gap-8"
        >
          {/* Trust framing — carries the Datora track record over to DevCore */}
          <p className="flex shrink-0 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground lg:max-w-[16rem]">
            <span className="text-accent">{t("trustBar.label")}</span>
            <ChevronDown
              size={14}
              className={`shrink-0 text-muted-foreground/70 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
            />
          </p>

          {/* Scrolling logo belt — hidden once the grid is expanded */}
          {!expanded && (
            <div className="marquee-mask relative min-w-0 flex-1">
              <div className="marquee-track marquee-left">
                {[...brands, ...brands].map((b, i) => (
                  <div
                    key={`${b.name}-${i}`}
                    className="flex items-center px-3.5 md:px-5"
                    aria-hidden={i >= brands.length}
                  >
                    <img
                      {...logoImg(b.src)}
                      alt={`${b.name} logo`}
                      loading="lazy"
                      decoding="async"
                      className={`h-5 w-auto md:h-6 object-contain opacity-55 ${b.invert ? "brightness-0 invert" : ""}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </button>

        {/* Expanded full grid of all partner logos */}
        {expanded && (
          <div className="mt-4 grid grid-cols-3 gap-x-4 gap-y-5 animate-fade-in sm:grid-cols-4 md:grid-cols-6 lg:mt-5">
            {brands.map((b) => (
              <div key={b.name} className="flex items-center justify-center">
                <img
                  {...logoImg(b.src)}
                  alt={`${b.name} logo`}
                  loading="lazy"
                  decoding="async"
                  className={`h-5 w-auto md:h-6 object-contain opacity-60 transition-opacity duration-300 hover:opacity-100 ${b.invert ? "brightness-0 invert" : ""}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrustMarquee;
